import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { ErrorOutline as ErrorOutlineIcon, Close as CloseIcon } from '@mui/icons-material'

export interface CsvErrorFlags {
  has_duplicate_rows?: boolean
  missing_lead_data?: boolean
  invalid_phone_format?: boolean
}

interface CsvValidationErrorDialogProps {
  open: boolean
  onClose: () => void
  onDownloadReport: () => void
  invalidCsvUrl: string | null
  errors: CsvErrorFlags
  /** The actual CSV column name the user mapped for phone (e.g. "Contact"). */
  phoneColumnName?: string
}

function buildErrorMessages(
  phoneCol: string,
): { key: keyof CsvErrorFlags; message: React.ReactNode }[] {
  return [
    {
      key: 'has_duplicate_rows',
      message: (
        <>
          <strong>Duplicate rows:</strong> Duplicate rows were found. Please remove them.
        </>
      ),
    },
    {
      key: 'missing_lead_data',
      message: (
        <>
          <strong>Missing phone numbers:</strong> Some phone numbers in <strong>&quot;{phoneCol}&quot;</strong> column
          are empty. Add a phone number for every lead.
        </>
      ),
    },
    {
      key: 'invalid_phone_format',
      message: (
        <>
          <strong>Invalid phone numbers:</strong> Some phone numbers in <strong>&quot;{phoneCol}&quot;</strong> column
          are invalid. Use Country Code followed by Phone Number. <br/>(e.g., 919876543210 for India, 14155552671 for USA)
        </>
      ),
    },
  ]
}

const CsvValidationErrorDialog: React.FC<CsvValidationErrorDialogProps> = ({
  open,
  onClose,
  onDownloadReport,
  invalidCsvUrl,
  errors,
  phoneColumnName = 'Contact',
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const errorMessages = buildErrorMessages(phoneColumnName)
  const activeErrors = errorMessages.filter(({ key }) => errors[key])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'relative',
          ...(isMobile ? { m: 0, width: '95%', maxWidth: '100%', borderRadius: 2 } : {}),
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 0.5, pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutlineIcon color="error" fontSize="small" />
          Errors found in your CSV
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            right: 12,
            top: 10,
            color: 'text.secondary',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={isMobile ? { p: 2, pt: 1 } : {}}>
        <DialogContentText sx={{ mb: 2, fontSize: isMobile ? '0.875rem' : 'inherit' }}>
          Some rows have errors that must be fixed before you can continue. Download the report to
          find the affected rows, then re-upload your CSV.
        </DialogContentText>

        <Box
          component="ul"
          sx={{ pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: isMobile ? 1 : 1.5 }}
        >
          {activeErrors.map(({ key, message }) => (
            <Box component="li" key={key}>
              <DialogContentText
                sx={{
                  color: 'text.primary',
                  fontSize: isMobile ? '0.875rem' : 'inherit',
                  wordBreak: 'break-word',
                }}
              >
                {message}
              </DialogContentText>
            </Box>
          ))}
        </Box>
      </DialogContent>

      {invalidCsvUrl && (
        <DialogActions
          sx={{
            p: 2,
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'stretch' : 'flex-end',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: 1.5,
            '& > :not(:first-of-type)': {
              ml: 0,
            },
          }}
        >
          <Button
            fullWidth={isMobile}
            variant="contained"
            color="primary"
            onClick={onDownloadReport}
            sx={{ py: isMobile ? 1.2 : 1, fontWeight: 600, width: isMobile ? '100%' : 'auto' }}
          >
            Download Report
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default CsvValidationErrorDialog
